"use client";

import { CodeBlock } from "./code-block";
import { SecretNote } from "./secret-note";
import { mcpConfig } from "../../lib/mcp-config";
import { CursorIcon } from "../ClientIcon";
import { ArrowRightIcon, BlocksIcon } from "lucide-react";

// // // //

/**
 * CursorInstructions
 * Setup for Cursor: a JSON snippet pasted into `~/.cursor/mcp.json` with the
 * API key in the Authorization header. Reused in placeholder mode (static
 * guide, copy off) and live mode (wizard, copy on with the real key).
 */
export function CursorInstructions(props: {
    apiKey: string;
    enableCopy?: boolean;
}) {
    const { apiKey, enableCopy = true } = props;

    const config = JSON.stringify(
        {
            mcpServers: {
                [mcpConfig.serverName]: {
                    url: mcpConfig.serverUrl,
                    headers: {
                        Authorization: `Bearer ${apiKey}`,
                    },
                },
            },
        },
        null,
        2,
    );

    return (
        <div className="flex flex-col gap-4">
            <ol className="flex flex-col gap-1.5 text-sm leading-relaxed text-muted-foreground">
                <Numbered n={1}>
                    In Cursor, open{" "}
                    <span className="text-foreground">Cursor Settings</span>.
                </Numbered>
                <Numbered n={1}>
                    Go to{" "}
                    <span className="text-foreground">
                        <BlocksIcon className="size-3 inline-block mr-1 ml-0.5 mb-px" />
                        Customize
                    </span>
                    <ArrowRightIcon className="size-3 inline-block mr-1 ml-0.5 mb-px" />
                    <span className="text-foreground">MCPs</span>, and click{" "}
                    <span className="text-foreground">New MCP Server</span> to
                    open <span className="text-foreground">mcp.json</span>.
                </Numbered>
                <Numbered n={3}>
                    Merge the JSON below into the{" "}
                    <span className="text-foreground font-mono">
                        mcpServers
                    </span>{" "}
                    object, then save the file.
                </Numbered>
                <Numbered n={4}>
                    Confirm the {mcpConfig.serverName} server is enabled and its
                    tools are listed.
                </Numbered>
            </ol>

            <div>
                <div className="mb-2 flex items-center gap-2">
                    <CursorIcon className="size-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">
                        Cursor
                    </span>
                </div>
                {/* Force a dark editor (dark bg + light text) regardless of theme. */}
                <CodeBlock
                    code={config}
                    enableCopy={enableCopy}
                    className="dark"
                />
            </div>

            {/* Only warn about the real key in the live wizard, not the
                placeholder preview. */}
            {enableCopy ? <SecretNote /> : null}
        </div>
    );
}

function Numbered({ n, children }: { n: number; children: React.ReactNode }) {
    return (
        <li className="flex gap-2.5">
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-secondary-foreground">
                {n}
            </span>
            <span>{children}</span>
        </li>
    );
}
